import { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { api, track } from "../lib/api";
import { usePricing, productOf, checkoutHref, bookingHref, attribution } from "../lib/catalog";
import Layout from "../components/Layout";
import ScoreRing from "../components/ScoreRing";
import CtaLink, { btnPrimary, btnOutline, btnOrange } from "../components/CtaLink";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { Loader2, AlertTriangle, Check, Zap, Lock, Link2, Mail, Linkedin, Twitter, MessageCircle } from "lucide-react";

const SITE = process.env.REACT_APP_SITE_URL;

const shareUrl = (id, source) => `${SITE}/score/${id}?utm_source=${source}&utm_medium=social&utm_campaign=score_share`;

function ShareButtons({ scan }) {
  const text = `We scored ${scan.overall_score}/100 on the AI Revenue Readiness Score™ for ${scan.domain}. Can AI assistants read your business? Check yours free:`;
  const links = [
    ["linkedin", "LinkedIn", Linkedin, (u) => `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(u)}`],
    ["x", "X", Twitter, (u) => `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(u)}`],
    ["whatsapp", "WhatsApp", MessageCircle, (u) => `https://wa.me/?text=${encodeURIComponent(`${text} ${u}`)}`],
  ];
  return (
    <div className="flex flex-wrap items-center justify-center md:justify-start gap-2 mt-8" data-testid="share-buttons">
      <span className="text-xs text-slate-400 mr-1">Share your score:</span>
      {links.map(([id, label, Icon, build]) => (
        <a key={id} href={build(shareUrl(scan.id, id))} target="_blank" rel="noopener noreferrer" data-testid={`share-${id}`}
          onClick={() => track("results.shared", { scan_id: scan.id, network: id })}
          className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-white/10 hover:bg-white/20 border border-white/15 text-white text-sm font-semibold transition-colors">
          <Icon className="h-4 w-4" />{label}
        </a>
      ))}
    </div>
  );
}

const SEV = { critical: "bg-rose-50 text-rose-600 border-rose-200", high: "bg-amber-50 text-amber-600 border-amber-200", medium: "bg-slate-100 text-slate-600 border-slate-200" };

function NextStep({ score, pricing }) {
  if (!pricing) return null;
  const P = (c) => productOf(pricing, c);
  const review = bookingHref(pricing.booking?.score_review_url);
  let headline, primary, secondary, tertiary;
  if (score < 50) {
    headline = "AI agents are likely skipping your business.";
    primary = [checkoutHref(P("agentic_ready")), "Make My Business Agentic Ready", btnPrimary, "next-primary-agentic"];
    secondary = [review, "Book a 15-min Score Review", btnOutline, "next-secondary-review"];
    tertiary = [checkoutHref(P("blueprint")), "Or start with the AI Readiness Blueprint — $497", "next-tertiary-blueprint"];
  } else if (score < 75) {
    headline = "You're partly readable — but AI agents still hit gaps.";
    primary = [checkoutHref(P("blueprint")), "Get My Blueprint — $497", btnOrange, "next-primary-blueprint"];
    secondary = [checkoutHref(P("get_found")), "Make My Business AI Ready (Get Found)", btnOutline, "next-secondary-getfound"];
  } else {
    headline = "You're ahead of most businesses.";
    primary = [checkoutHref(P("get_found")), "Stay ahead with Get Found monitoring", btnPrimary, "next-primary-getfound"];
    secondary = [checkoutHref(P("blueprint")), "Get My Blueprint — $497", btnOutline, "next-secondary-blueprint"];
  }
  return (
    <div className="rounded-2xl border border-indigo-200 bg-white p-6 shadow-sm" data-testid="next-step">
      <p className="font-mono text-xs uppercase tracking-wide text-indigo-600">Your recommended next step</p>
      <h3 className="font-heading text-xl font-bold text-slate-900 mt-1" data-testid="next-step-headline">{headline}</h3>
      <div className="mt-5 flex flex-col gap-3">
        <CtaLink to={primary[0]} className={`${primary[2]} w-full`} testid={primary[3]} event={{ name: "results.cta", props: { cta: primary[3] } }}>{primary[1]}</CtaLink>
        <CtaLink to={secondary[0]} className={`${secondary[2]} w-full`} testid={secondary[3]} event={{ name: "results.cta", props: { cta: secondary[3] } }}>{secondary[1]}</CtaLink>
        {tertiary && <CtaLink to={tertiary[0]} className="text-sm text-indigo-600 font-semibold text-center hover:underline" testid={tertiary[2]}>{tertiary[1]}</CtaLink>}
      </div>
    </div>
  );
}

function EmailGate({ scanId, source, onUnlocked }) {
  const [form, setForm] = useState({ first_name: "", email: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.email) { toast.error("First name and email are required"); return; }
    setBusy(true);
    try {
      const attr = attribution();
      const { data } = await api.post(`/scan/${scanId}/lead`, { ...form, source, utm: attr.utm || {}, referrer: attr.referrer || null });
      localStorage.setItem("ar_lead_email", form.email);
      localStorage.setItem("ar_lead_id", data.lead_id);
      toast.success("Full report unlocked — a copy is on its way to your inbox.");
      onUnlocked();
    } catch { toast.error("Could not submit. Please retry."); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="rounded-2xl border border-slate-200 bg-white p-7" data-testid="report-gate">
      <div className="flex items-center gap-2 text-slate-500"><Lock className="h-4 w-4" /><span className="text-xs font-mono uppercase tracking-wide">Detailed findings locked</span></div>
      <h3 className="font-heading text-xl font-bold text-slate-900 mt-2">Email me my full report</h3>
      <p className="text-sm text-slate-500 mt-1">See exactly what AI understands, where it struggles, your five most important gaps and your biggest transaction blocker.</p>
      <div className="grid sm:grid-cols-2 gap-3 mt-5">
        <Input data-testid="lead-firstname" placeholder="First name" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="h-12" />
        <Input data-testid="lead-email" type="email" placeholder="Work email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-12" />
      </div>
      <Button type="submit" disabled={busy} data-testid="lead-submit-btn" className="mt-4 h-12 px-7 rounded-full bg-indigo-600 hover:bg-indigo-700 font-semibold">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Mail className="h-4 w-4 mr-2" />Email me my full report</>}
      </Button>
    </form>
  );
}

function Findings({ r }) {
  return (
    <div className="space-y-10" data-testid="detailed-findings">
      <div className="grid sm:grid-cols-2 gap-6">
        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <h3 className="font-heading font-semibold text-slate-900 flex items-center gap-2"><Check className="h-4 w-4 text-emerald-500" /> What AI can understand</h3>
          <ul className="mt-3 space-y-2 text-sm text-slate-600">{(r.what_ai_understands || []).map((x, i) => <li key={i} className="flex gap-2"><span className="text-emerald-500">•</span>{x}</li>)}</ul>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <h3 className="font-heading font-semibold text-slate-900 flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-amber-500" /> What AI may struggle with</h3>
          <ul className="mt-3 space-y-2 text-sm text-slate-600">{(r.what_ai_struggles || []).map((x, i) => <li key={i} className="flex gap-2"><span className="text-amber-500">•</span>{x}</li>)}</ul>
        </div>
      </div>
      <div>
        <h3 className="font-heading text-xl font-bold text-slate-900">Three strengths</h3>
        <div className="mt-4 grid sm:grid-cols-3 gap-4">{(r.strengths || []).map((s, i) => <div key={i} className="rounded-xl border border-emerald-200 bg-emerald-50/50 p-4 text-sm text-slate-700">{s}</div>)}</div>
      </div>
      <div>
        <h3 className="font-heading text-xl font-bold text-slate-900">Five most important gaps</h3>
        <div className="mt-4 space-y-3">
          {(r.gaps || []).map((g, i) => (
            <div key={i} className="rounded-xl border border-slate-200 bg-white p-5" data-testid={`gap-${i}`}>
              <div className="flex items-start justify-between gap-3"><p className="font-semibold text-slate-900">{g.title}</p><span className={`text-[11px] font-mono uppercase px-2 py-0.5 rounded-full border ${SEV[g.severity] || SEV.medium}`}>{g.severity}</span></div>
              <p className="text-sm text-slate-500 mt-1.5">{g.business_description}</p>
            </div>
          ))}
        </div>
      </div>
      <div className="rounded-2xl border-2 border-rose-200 bg-rose-50/40 p-6">
        <h3 className="font-heading font-bold text-slate-900 flex items-center gap-2"><Zap className="h-4 w-4 text-rose-500" /> Most important transaction blocker</h3>
        <p className="text-slate-700 mt-2">{r.transaction_blocker}</p>
      </div>
    </div>
  );
}

function BadgeEmbed({ scan }) {
  const origin = window.location.origin;
  const img = `${origin}/api/badge/${scan.id}.svg`;
  const snippet = `<a href="${origin}/score/${scan.id}?utm_source=badge&utm_medium=embed&utm_campaign=${scan.domain}" target="_blank" rel="noopener"><img src="${img}" alt="AI Readiness Score ${scan.overall_score}/100 for ${scan.domain}" width="240" height="64" /></a> <a href="${origin}/?utm_source=badge">Scored by Make My Business AI Ready</a>`;
  const copy = () => { navigator.clipboard?.writeText(snippet); toast.success("Badge code copied"); track("badge.copied", { scan_id: scan.id }); };
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6 mt-6" data-testid="badge-embed">
      <p className="font-mono text-xs uppercase tracking-wide text-indigo-600">Show off your score</p>
      <h3 className="font-heading font-bold text-slate-900 mt-1">Add the AI Readiness Score badge to your site</h3>
      <img src={img} alt={`AI Readiness Score ${scan.overall_score}/100`} width={240} height={64} className="mt-4" data-testid="badge-preview" />
      <textarea readOnly value={snippet} rows={4} className="mt-4 w-full rounded-lg border border-slate-200 bg-slate-50 p-3 font-mono text-[11px] text-slate-600" data-testid="badge-snippet" />
      <button onClick={copy} className={`${btnOutline} mt-3 w-full`} data-testid="badge-copy-btn">Copy badge code</button>
    </div>
  );
}

export default function Results() {
  const { scanId } = useParams();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { data: pricing } = usePricing();
  const [scan, setScan] = useState(null);
  const [err, setErr] = useState(false);
  const isShare = pathname.startsWith("/score/");

  const load = async (tries = 0) => {
    try {
      const { data } = await api.get(`/scan/${scanId}`);
      if (data.status === "completed") { setScan(data); return; }
      if (data.status === "failed") { setErr(true); return; }
      if (tries < 40) setTimeout(() => load(tries + 1), 1500);
    } catch { setErr(true); }
  };
  useEffect(() => { load(); localStorage.setItem("ar_last_scan", scanId); }, [scanId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isShare) return undefined;
    const m = document.createElement("meta"); m.name = "robots"; m.content = "noindex, nofollow"; document.head.appendChild(m);
    return () => { m.remove(); };
  }, [isShare]);

  if (err) return <Layout><div className="text-center py-32"><p className="text-slate-600">We couldn't complete this scan. <button className="text-indigo-600 font-semibold" onClick={() => navigate("/free-scan")}>Try again</button></p></div></Layout>;
  if (!scan) return <Layout><div className="flex flex-col items-center py-40"><Loader2 className="h-8 w-8 text-indigo-600 animate-spin" /><p className="text-slate-500 mt-4">Loading your results…</p></div></Layout>;

  const r = scan.result;
  const statusColor = r.overall_score >= 75 ? "text-emerald-500" : r.overall_score >= 50 ? "text-amber-500" : "text-rose-500";
  const share = () => { navigator.clipboard?.writeText(shareUrl(scan.id, "copy_link")); toast.success("Share link copied"); track("results.shared", { scan_id: scan.id, network: "copy_link" }); };

  return (
    <Layout assistantContext={{ domain: scan.domain, score: r.overall_score, recommended_plan: r.recommended_plan }}>
      {isShare && (
        <div className="bg-orange-500 text-white text-sm font-semibold text-center py-2.5 px-4" data-testid="share-visitor-banner">
          Someone shared their AI Readiness Score with you. <button onClick={() => navigate("/free-scan")} className="underline underline-offset-2 ml-1" data-testid="share-visitor-scan">Get your own free score →</button>
        </div>
      )}
      <div className="bg-slate-950 text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <div className="flex items-center justify-between gap-4">
            <p className="font-mono text-xs uppercase tracking-widest text-indigo-300">AI Revenue Readiness Score™</p>
            <button onClick={share} className="text-xs text-slate-300 hover:text-white inline-flex items-center gap-1.5" data-testid="share-score-btn"><Link2 className="h-3.5 w-3.5" /> Copy share link</button>
          </div>
          <div className="mt-6 flex flex-col md:flex-row items-center gap-10">
            <div data-testid="overall-score"><ScoreRing score={r.overall_score} size={190} dark /></div>
            <div className="flex-1 text-center md:text-left">
              <p className="font-mono text-sm text-slate-400">{scan.domain}</p>
              <p className={`font-heading text-3xl font-bold mt-1 ${statusColor}`} data-testid="results-status">{r.status}</p>
              <p className="mt-3 text-slate-300 max-w-xl">{r.business_impact}</p>
              <ShareButtons scan={scan} />
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mt-10">
            {r.category_meta.map((c) => (
              <div key={c.key} className="rounded-xl bg-slate-900/60 border border-slate-800 p-4" data-testid={`cat-${c.key}`}>
                <p className="text-xs text-slate-400">{c.name}</p>
                <p className={`font-heading text-2xl font-bold mt-1 ${c.score >= 60 ? "text-emerald-400" : c.score >= 40 ? "text-amber-400" : "text-rose-400"}`}>{c.score}</p>
                <div className="h-1.5 rounded-full bg-slate-800 mt-2 overflow-hidden"><div className="h-full rounded-full bg-indigo-500" style={{ width: `${c.score}%` }} /></div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2">
          {scan.unlocked ? <Findings r={r} /> : <EmailGate scanId={scan.id} source={isShare ? "score_share" : "free_scan"} onUnlocked={() => load()} />}
        </div>
        <aside className="lg:sticky lg:top-20 h-fit"><NextStep score={r.overall_score} pricing={pricing} /><BadgeEmbed scan={scan} /></aside>
      </div>
    </Layout>
  );
}
